// Refonte du suivi — geste 2 : modifier le brouillon (lot 4, revu après la
// revue des lots 3-4).
//
// S'ouvre directement sur « Semaine, menus & recettes » (WeekMenusEditor
// repris tel quel). Le plan et les compléments restent accessibles, en
// second. Tout s'enregistre seul ; l'indicateur dit où on en est et, en cas
// de souci, pourquoi. Une modification refusée est mise de côté (Réessayer ou
// Retirer) ; un conflit (autre onglet) ou une coupure ne fait jamais perdre
// une modification.

import { useCallback, useEffect, useState } from 'react';
import WeekMenusEditor from '../components/WeekMenusEditor';
import PropositionsIA from './PropositionsIA';
import { LIBELLES_CHAMP, LIBELLES_MOTIF, ONGLETS_MODIFIER, creerAdaptateurSemaine, nomPhase } from './suiviEcran';
import useSuiviAutosave, { ETATS_AUTOSAVE } from './services/useSuiviAutosave';

function Indicateur({ statut, libelle, erreur, onReessayer }) {
  const alerte = statut === ETATS_AUTOSAVE.CONFLIT || statut === ETATS_AUTOSAVE.ERREUR || statut === ETATS_AUTOSAVE.HORS_LIGNE;
  return (
    <p className={alerte ? 'jrn-error suivi-statut' : 'jrn-meta-chip suivi-statut'} role="status" aria-live="polite">
      {libelle}
      {alerte && statut !== ETATS_AUTOSAVE.CONFLIT && erreur?.message ? ` : ${erreur.message}` : ''}
      {statut === ETATS_AUTOSAVE.ERREUR && onReessayer && (
        <>
          {' '}
          <button type="button" className="jrn-btn jrn-btn--ghost" onClick={onReessayer}>Réessayer</button>
        </>
      )}
    </p>
  );
}

function ChampTexte({ label, champ, valeurInitiale, onModifier }) {
  const [valeur, setValeur] = useState(valeurInitiale ?? '');
  return (
    <label className="jrn-label suivi-modifier__champ">
      {label}
      <textarea
        rows={14}
        value={valeur}
        onChange={(event) => {
          setValeur(event.target.value);
          onModifier({ [champ]: event.target.value });
        }}
      />
    </label>
  );
}

const nomsChamps = (changes) => Object.keys(changes).map((cle) => LIBELLES_CHAMP[cle] || cle).join(', ');

export default function Modifier({
  brouillon,
  client,
  nomVersion = 'V2',
  repris = false,
  ongletInitial = 'semaine',
  onRecharger,
  onAbandonner,
  onValider,
  onDirtyChange,
  changementsInitiaux = null,
  // Lot 7 : faits du dernier rendez-vous, pour les propositions de l'IA.
  ressenti = null,
  enregistrer,
}) {
  const autosave = useSuiviAutosave(brouillon, enregistrer ? { enregistrer } : {});
  const { etat } = autosave;
  const ligne = etat.ligne;
  const [onglet, setOnglet] = useState(ongletInitial);
  const [affichage, setAffichage] = useState(0);
  const [confirmerAbandon, setConfirmerAbandon] = useState(false);
  const [abandonEnCours, setAbandonEnCours] = useState(false);
  const [erreurAbandon, setErreurAbandon] = useState(null);
  const [erreurValider, setErreurValider] = useState(null);
  const conflit = etat.statut === ETATS_AUTOSAVE.CONFLIT;
  const motif = ligne?.suivi_motif || brouillon?.motif || null;
  const phaseCible = ligne?.followup_data?.suivi_phase_cible?.to || ligne?.followup_data?.phaseTransitionDraft?.toPhaseId || null;
  const nomPhaseCible = phaseCible
    ? nomPhase((ligne?.protocol_phases?.phases || []).find((phase) => String(phase?.id) === String(phaseCible)))
    : null;
  const [menusModifies, setMenusModifies] = useState(false);
  const signalerMenus = useCallback((_section, sale) => setMenusModifies(sale === true), []);
  const nonEnregistre = autosave.nonEnregistre || menusModifies;

  // Changements à porter dès l'ouverture (texte « à sa demande ») : ils
  // partent par la file normale, donc visibles et réessayés.
  useEffect(() => {
    if (changementsInitiaux) autosave.modifier(changementsInitiaux);
    // Une seule fois par brouillon (le composant est monté par version).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // La fiche sait qu'il reste quelque chose à enregistrer (navigation bloquée).
  useEffect(() => {
    onDirtyChange?.(nonEnregistre, { menus: menusModifies, autres: autosave.nonEnregistre });
  }, [nonEnregistre, menusModifies, autosave.nonEnregistre, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  // Les menus modifiés dans l'éditeur (pas encore enregistrés par lui) :
  // même avertissement à la fermeture de l'onglet que le reste.
  useEffect(() => {
    if (!menusModifies || typeof window === 'undefined') return undefined;
    const avertir = (event) => { event.preventDefault(); event.returnValue = ''; return ''; };
    window.addEventListener('beforeunload', avertir);
    return () => window.removeEventListener('beforeunload', avertir);
  }, [menusModifies]);

  function changerOnglet(id) {
    if (onglet === 'semaine' && id !== 'semaine' && menusModifies && typeof window !== 'undefined'
        && !window.confirm('Les menus ont des modifications pas encore enregistrées. Elles restent dans « Semaine, menus & recettes ». Changer d’onglet ?')) return;
    setOnglet(id);
  }

  const sauverSemaine = creerAdaptateurSemaine({
    lireLigne: () => autosave.lireEtat().ligne,
    enregistrerMaintenant: autosave.enregistrerMaintenant,
  });
  // Base d'une proposition appliquée : la ligne relue une fois la file
  // d'enregistrement vidée ; null si quelque chose reste en attente.
  async function lireLigneEnregistree() {
    try {
      await autosave.enregistrerMaintenant();
    } catch {
      return null;
    }
    if (autosave.aDuNonEnregistre() || autosave.lireEtat().statut === ETATS_AUTOSAVE.CONFLIT) return null;
    return autosave.lireEtat().ligne || null;
  }
  const valeurAffichee = (champ) => (champ in etat.enAttente ? etat.enAttente[champ] : ligne?.[champ]);
  const rafraichirChamps = () => setAffichage((n) => n + 1);

  async function recharger() {
    const relue = await onRecharger?.();
    if (relue) {
      autosave.recharger(relue, { reprendre: true });
      rafraichirChamps();
    }
  }

  // Tout ce qui est en attente part d'abord : on valide ce qui est en base.
  async function allerValider() {
    setErreurValider(null);
    if (menusModifies) {
      setErreurValider('Les menus ont des modifications pas encore enregistrées : enregistre-les dans « Semaine, menus & recettes », puis valide.');
      return;
    }
    try {
      await autosave.enregistrerMaintenant();
    } catch {
      // La raison est affichée par l'indicateur.
    }
    if (autosave.aDuNonEnregistre() || autosave.lireEtat().statut === ETATS_AUTOSAVE.CONFLIT) {
      setErreurValider('Tes dernières modifications ne sont pas encore enregistrées. Règle-les ci-dessus, puis valide.');
      return;
    }
    onValider?.(autosave.lireEtat().ligne);
  }

  // L'enregistrement automatique s'arrête d'abord (l'envoi en cours se
  // termine) : l'abandon part avec la dernière révision. S'il échoue, on
  // repart de la ligne la plus récente, modifications en attente comprises.
  async function abandonner() {
    setAbandonEnCours(true);
    setErreurAbandon(null);
    const derniere = await autosave.arreter();
    try {
      await onAbandonner?.(derniere);
      autosave.oublierLocal();
    } catch (error) {
      autosave.reactiver();
      let message = error?.message || 'Abandon impossible.';
      if (error?.ligne) {
        autosave.recharger(error.ligne, { reprendre: true });
        rafraichirChamps();
        if (!String(derniere?.nutrition_plan || '').trim() && String(error.ligne.nutrition_plan || '').trim()) {
          message += ' Le brouillon contient maintenant le plan de la version précédente.';
        }
      }
      // Les modifications en attente repartent.
      autosave.relancer();
      setErreurAbandon(message);
      setAbandonEnCours(false);
    }
  }

  return (
    <section className="jrn-block suivi-modifier">
      <div className="jrn-block__head">
        <h3 className="jrn-block__title">{nomVersion} en préparation</h3>
        <Indicateur statut={etat.statut} libelle={autosave.libelle} erreur={etat.erreur} onReessayer={() => autosave.reessayer().catch(() => {})} />
      </div>
      <p className="jrn-block__intro">
        {LIBELLES_MOTIF[motif] || 'Nouvelle version'}
        {motif === 'changement_phase' && nomPhaseCible ? ` · « ${nomPhaseCible} » commencera à la publication` : ''}
        {repris ? ' · brouillon repris de l’ancien écran' : ''}
      </p>

      {etat.restaure && <p className="jrn-surface suivi-modifier__restaure" role="status">{etat.restaure}</p>}

      {conflit && (
        <div className="jrn-surface suivi-modifier__conflit" role="alert">
          <p>{etat.erreur?.message}</p>
          <button type="button" className="jrn-btn jrn-btn--primary" onClick={() => recharger().catch(() => {})}>
            Recharger
          </button>
        </div>
      )}

      {etat.refuses.map((refus) => (
        <div key={refus.id} className="jrn-surface suivi-modifier__refus" role="alert">
          <p>
            Pas enregistré ({nomsChamps(refus.changes)}) : {refus.message}
          </p>
          <button type="button" className="jrn-btn jrn-btn--ghost" onClick={() => autosave.reessayerRefus(refus.id).catch(() => {})}>
            Réessayer
          </button>
          <button
            type="button"
            className="jrn-btn jrn-btn--soft"
            onClick={() => { autosave.retirerRefus(refus.id); rafraichirChamps(); }}
          >
            Retirer cette modification
          </button>
        </div>
      ))}

      {etat.choix.map((choix) => (
        <div key={choix.champ} className="jrn-surface suivi-modifier__choix" role="alert">
          <p>« {LIBELLES_CHAMP[choix.champ] || choix.champ} » a aussi été modifié dans l’autre onglet. Quelle version garder ?</p>
          <button type="button" className="jrn-btn jrn-btn--primary" onClick={() => { autosave.garderMaVersion(choix.champ); rafraichirChamps(); }}>
            Garder ma version
          </button>
          <button type="button" className="jrn-btn jrn-btn--ghost" onClick={() => { autosave.garderAutreVersion(choix.champ); rafraichirChamps(); }}>
            Garder l’autre version
          </button>
        </div>
      ))}

      <div className="jrn-actions" role="tablist">
        {ONGLETS_MODIFIER.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={onglet === item.id}
            className={`jrn-btn ${onglet === item.id ? 'jrn-btn--primary' : 'jrn-btn--ghost'}`}
            onClick={() => changerOnglet(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {onglet === 'semaine' && (
        <PropositionsIA
          client={client}
          ligne={ligne}
          rdv={ressenti?.rdv || null}
          donnees={ressenti?.donnees || null}
          depuis={ressenti?.depuis || null}
          motif={motif}
          menusModifies={menusModifies}
          onAppliquer={(consultation) => sauverSemaine(consultation, { kind: 'content' })}
          lireLigne={lireLigneEnregistree}
        />
      )}

      {/* Toujours monté (caché hors de son onglet) : ses modifications en
          cours et sa fusion après un conflit ne disparaissent jamais. */}
      <div hidden={onglet !== 'semaine'}>
        <WeekMenusEditor
          client={client}
          consultation={ligne}
          onSave={sauverSemaine}
          onDirtyChange={signalerMenus}
          disabled={conflit || abandonEnCours}
          alreadyPublished={false}
          authorityStale={false}
          reclaimsSource={false}
        />
      </div>
      {onglet === 'plan' && (
        <ChampTexte
          key={`plan-${ligne?.id}-${affichage}`}
          label="Plan"
          champ="nutrition_plan"
          valeurInitiale={valeurAffichee('nutrition_plan')}
          onModifier={autosave.modifier}
        />
      )}
      {onglet === 'complements' && (
        <ChampTexte
          key={`complements-${ligne?.id}-${affichage}`}
          label="Compléments"
          champ="supplements"
          valeurInitiale={valeurAffichee('supplements')}
          onModifier={autosave.modifier}
        />
      )}

      <div className="jrn-actions suivi-modifier__fin">
        <button type="button" className="jrn-btn jrn-btn--primary" disabled={!onValider || conflit || abandonEnCours} onClick={() => allerValider()}>
          Valider et publier
        </button>
        {!confirmerAbandon ? (
          <button type="button" className="jrn-btn jrn-btn--ghost" onClick={() => setConfirmerAbandon(true)}>
            Abandonner ce brouillon
          </button>
        ) : (
          <span className="suivi-modifier__abandon">
            Le brouillon disparaît, la version publiée reste en place.{' '}
            <button type="button" className="jrn-btn jrn-btn--soft" disabled={abandonEnCours} onClick={() => abandonner()}>
              {abandonEnCours ? 'Abandon…' : 'Oui, abandonner'}
            </button>
            <button type="button" className="jrn-btn jrn-btn--ghost" disabled={abandonEnCours} onClick={() => setConfirmerAbandon(false)}>
              Garder
            </button>
          </span>
        )}
      </div>
      {erreurValider && <p className="jrn-error" role="alert">{erreurValider}</p>}
      {erreurAbandon && <p className="jrn-error" role="alert">{erreurAbandon}</p>}
    </section>
  );
}
